import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { reactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import sanitizeHtml from 'sanitize-html'
import { v4 as newGuid } from 'uuid'
import {
    PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING as ShortcutKeys,
    TextVariant,
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as TextVariants,
} from '../constants'
import {
    PropertyEditorEditableTextProps,
    extractUpdatedElementNodes,
} from './PropertyEditorEditableText'
const {
    InsertPill,
    InsertUOM,
    InsertText,
    AddSpecifierNote,
    SelectAll,
    Copy,
    Save,
    Close,
    Undo,
    Tab,
    Promote,
    Demote,
    Escape,
    Backspace,
    Delete,
    ArrowRight,
    ArrowLeft,
    ArrowUp,
    ArrowDown,
    MoveUp,
    MoveDown,
    Export,
    Preview,
    SupportingDocs,
    CopyPT,
    Enter,
} = ShortcutKeys

/**
 * Checks if a text contains any of the blocked characters.
 *
 * @param {string} text - The text to check.
 * @param {string[]} blockedChars - The array of characters to block.
 *
 * @returns {boolean} Returns true if the text contains any blocked character, false otherwise.
 *
 * @example
 * // Example usage of containsBlockedCharacters
 * containsBlockedCharacters('hello', ['a', 'b', 'c']) // returns false
 * containsBlockedCharacters('hello', ['a', 'b', 'c', 'h']) // returns true
 */
export const containsBlockedCharacters = (
    text: string,
    blockedChars: string[]
) => {
    for (let i = 0; i < blockedChars.length; i++) {
        const blockedChar = blockedChars[i]
        if (text.includes(blockedChar)) {
            return true
        }
    }
    return false
}

export const superSanitizeHtml = (text: string) => {
    return sanitizeHtml(text, { allowedTags: [] })
}

/**
 * Returns the shortcut key for a given event and key.
 *
 * @param {keyof typeof ShortcutKeys} name - The key to check.
 * @param {React.KeyboardEvent<HTMLDivElement> | KeyboardEvent} event - The keyboard event to check.
 *
 * @returns {typeof ShortcutKeys[keyof typeof ShortcutKeys] | ''} The mapped shortcut value if it exists, empty string otherwise.
 *
 * @example
 * // Example usage of tryGetShortcutKey
 * tryGetShortcutKey('InsertUOM', event) // returns value of SHORTCUT_KEYS['InsertUOM'] if event.ctrlKey and event.shiftKey are true and event.key is '>'
 */
export const tryGetShortcutKey = (
    name: keyof typeof ShortcutKeys,
    event: React.KeyboardEvent<HTMLDivElement> | KeyboardEvent
): typeof ShortcutKeys[keyof typeof ShortcutKeys] | '' => {
    const { key: inputKey, ctrlKey: Ctrl, altKey: Alt, shiftKey: Shift } = event
    const NotFound = ''

    // utility func to get shortcut key by comparing expected and mapped value.
    const tryGet = (expectedShortcutValue: string) =>
        areEq(expectedShortcutValue, ShortcutKeys[name])
            ? ShortcutKeys[name]
            : NotFound

    // Ctrl + Alt + key
    if (Ctrl) {
        // Ctrl + Alt + key
        if (Alt && !Shift) {
            switch (inputKey.toUpperCase()) {
                case 'C':
                    return tryGet(CopyPT)
                case 'I':
                    return tryGet(Preview)
                case 'S':
                    return tryGet(AddSpecifierNote)
                case 'P':
                    return tryGet(InsertPill)
                case 'T':
                    return tryGet(InsertText)
                default:
                    break
            }
        }
        // Ctrl + Shift + key
        if (Shift && !Alt) {
            switch (inputKey.toUpperCase()) {
                case '>':
                case '.': // use . (dot) to support for MacOS
                    console.warn(
                        `Ctrl+Shift+> is be deprecated. Use ${tryGet(
                            InsertUOM
                        )} to insert uom instead.`
                    )
                    return NotFound
                case '<':
                case ',': // use , (comma) to support for MacOS
                    console.warn(
                        `Ctrl+Shift+< is be deprecated. Use ${tryGet(
                            InsertText
                        )} to insert text instead.`
                    )
                    return NotFound
                case 'E':
                    return tryGet(Export)
                default:
                    break
            }
        }
        // Ctrl + key
        if (!Alt && !Shift) {
            switch (inputKey.toUpperCase()) {
                case '[':
                    console.warn(
                        `Ctrl+[ will be deprecated. Use ${tryGet(
                            InsertPill
                        )} to insert pill instead.`
                    )
                    return NotFound
                case 'A':
                    return tryGet(SelectAll)
                case 'C':
                    return tryGet(Copy)
                case 'I':
                    return tryGet(SupportingDocs)
                case 'S':
                    return tryGet(Save)
                case 'U':
                    return tryGet(InsertUOM)
                case 'X':
                    return tryGet(Close)
                case 'Z':
                    return tryGet(Undo)
                default:
                    break
            }
        }
    }
    // Alt + Shift + key
    else if (Alt) {
        if (Shift) {
            switch (inputKey.toUpperCase()) {
                case ArrowUp.toUpperCase():
                    return tryGet(MoveUp)
                case ArrowDown.toUpperCase():
                    return tryGet(MoveDown)
                default:
                    break
            }
        }
    }
    // Shift + key
    else if (Shift && !Alt && !Ctrl) {
        if (areEq(inputKey, Tab)) {
            return tryGet(Promote)
        }
    } else if (!Ctrl && !Shift && !Alt && areEq(inputKey, Demote)) {
        return tryGet(Demote)
    }
    // other key mapping
    else if (areEq(inputKey, name)) {
        return tryGet(ShortcutKeys[name])
    }

    return NotFound
}

// export const tryGetShortcutAction = keyof type

/**
 * Checks if a key is not a navigation key.
 *
 * @param {string} inputKey - The key to check.
 *
 * @returns {boolean} Returns true if the key is not a navigation key, false otherwise.
 *
 * @example
 * // Example usage of isNonNavigationKey
 * isNonNavigationKey('a') // returns true
 * isNonNavigationKey('ArrowDown') // returns false
 */
const isNonNavigationKey = (inputKey: string) => {
    // Check if the key is one of the navigation keys
    return (
        inputKey.length > 1 &&
        !areEq(inputKey, Escape) &&
        !areEq(inputKey, Backspace) &&
        !areEq(inputKey, Delete) &&
        !areEq(inputKey, ArrowRight) &&
        !areEq(inputKey, ArrowLeft) &&
        !areEq(inputKey, ArrowUp) &&
        !areEq(inputKey, ArrowDown) &&
        !areEq(inputKey, Enter)
    )
}

/**
 * Prevents propagation of an event if it contains blocked characters or is a non-navigation key.
 *
 * @param {React.KeyboardEvent<HTMLDivElement> | KeyboardEvent} event - The keyboard event to check.
 * @param {string[]} blockedCharacters - The array of characters to block.
 *
 * @example
 * // Example usage of shouldHaltKeyboardEvent
 * shouldHaltKeyboardEvent(event, ['a', 'b', 'c'])
 */
export const shouldHaltKeyboardEvent = (
    event: React.KeyboardEvent<HTMLDivElement> | KeyboardEvent,
    blockedCharacters: string[]
) => {
    // Check if the event key is a blocked character or a non-navigation key
    if (
        containsBlockedCharacters(event.key, blockedCharacters) ||
        isNonNavigationKey(event.key)
    ) {
        haltEvent(event)
    }
}

type Truthy<T> = T extends null | undefined | '' | 0 | false ? never : T
type Nullable<T> = T | null | undefined
/**
 * Checks if two strings are equal (case-insensitive).
 *
 * @param {Nullable<string>} str1 - The first string to compare.
 * @param {Nullable<string>} str2 - The second string to compare.
 * @returns {boolean} - Returns true if the strings are equal (case-insensitive), false otherwise.
 */
export const areEq = (
    str1: Nullable<string>,
    str2: Nullable<string>
): boolean => {
    if (str1 != null && str2 != null) {
        return (
            str1
                .trim()
                .localeCompare(str2.trim(), undefined, {
                    sensitivity: 'base',
                }) === 0
        )
    }

    return false
}

/**
 * Halts the propagation of an event and prevents its default behavior.
 *
 * @param {Pick<Event, 'preventDefault' | 'stopPropagation'>} event - The event with halt methods to be executed.
 */
export const haltEvent = (
    event: Pick<Event, 'preventDefault' | 'stopPropagation'>
) => {
    event.preventDefault()
    event.stopPropagation()
}

/**
 * Simulates a key press event.
 *
 * @param {typeof ShortcutKeys[keyof typeof ShortcutKeys]} keyCombination - The key combination to simulate.
 *
 * This function simulates a key press event based on the provided key combination. The key combination can include 'ctrl', 'alt', and 'shift'.
 * It then dispatches a new KeyboardEvent with the parsed keys.
 */
export const simulateKeyPress = (
    keyCombination: typeof ShortcutKeys[keyof typeof ShortcutKeys],
    refElement?: any
) => {
    let parsedKeys = {
        ctrlKey: false,
        altKey: false,
        shiftKey: false,
        key: '',
    }

    // parse key combination
    keyCombination.split('+').map((value) => {
        const isCtrl = areEq(value, 'Ctrl')
        const isShift = areEq(value, 'Shift')
        const isAlt = areEq(value, 'Alt')
        const isNonNavigationKey =
            value.length > 0 && !isCtrl && !isShift && !isAlt

        parsedKeys = {
            ctrlKey: isCtrl || parsedKeys.ctrlKey,
            altKey: isAlt || parsedKeys.altKey,
            shiftKey: isShift || parsedKeys.shiftKey,
            key: isNonNavigationKey ? value : parsedKeys.key,
        }
    })
    const element = refElement || document
    // register keydown keyboard event
    window.dispatchEvent(new KeyboardEvent('keydown', parsedKeys))
}

export const sanitizeZeroWidthText = (val: string, replaceBy: string = '') => {
    return val.replace(/\u200d/g, replaceBy)
}

export const insertPillTextUOM = ({
    event,
    pillType,
    insertPillIndex,
    variant,
    titleText,
    value,
    template,
    element,
    changeType,
    fieldState,
    setFieldState,
    setProperty,
    saveChanges,
    isNotFromContentTeam,
}: {
    event?:
        | Pick<React.KeyboardEvent, 'currentTarget' | 'target'>
        | Pick<React.MouseEvent, 'currentTarget' | 'target'>
    insertPillIndex: number
    pillType: TextVariant
} & Partial<PropertyEditorEditableTextProps>) => {
    const target: any = event?.currentTarget ?? event?.target
    const { insertPill } = reactiveWorkspaceActions()

    if (
        !areEq(value, target?.textContent) &&
        !areEq(variant, TextVariants.newlyadded)
    ) {
        //This will update the template here itself
        //if the user  enter a text and then create a pill
        const eventElementNodes = Array.from(
            target?.childNodes as NodeList
        )?.map((eventNode) => {
            if (eventNode.nodeType === 1) {
                return {
                    id: (eventNode as any).id,
                    textContent: sanitizeZeroWidthText(eventNode?.textContent!),
                    outerText: sanitizeZeroWidthText(
                        (eventNode as any).outerText
                    ),
                }
            }
            return eventNode
        })

        let newTextContentElement = extractUpdatedElementNodes(
            eventElementNodes,
            element!,
            changeType,
            titleText,
            isNotFromContentTeam,
            template?.isUserAdded
        )?.filter((element: TextContentElement) => {
            return element?.text !== '' && !element.text?.match(/\u200d/g)
        })

        const addedOnly = newTextContentElement?.every(
            (x: any) => x?.changeType?.toUpperCase() === 'ADDED'
        )
        if (addedOnly) {
            // handle inserting pill between user-added content elements only
            fieldState!.elements = [...newTextContentElement]
            setFieldState?.(fieldState)
            insertPill?.({
                fieldState,
                template,
                insertPillIndex,
                pillType,
                textWithPill: target?.textContent ?? '',
            })
        } else {
            // handle inserting pill between user-added and base content elements
            const contentIndex = template?.contentItem?.elements?.findIndex(
                (elem) => elem.id === newTextContentElement?.[0]?.id
            )

            // find the user-added content
            const addedTextIndex =
                newTextContentElement?.findIndex(
                    (elem: any) =>
                        elem?.type?.toUpperCase() === 'TEXT' &&
                        elem?.changeType?.toUpperCase() === 'ADDED'
                ) ?? newTextContentElement?.length

            // insert the new pill next to user-added content. Limit by array length.
            const newPillIndex = Math.min(
                addedTextIndex + 1,
                newTextContentElement?.length
            )

            newTextContentElement?.splice(newPillIndex, 0, {
                elements: [],
                id: newGuid(),
                imperialUnits: '',
                metricUnits: '',
                text: '',
                type: pillType,
                placeholderText: '',
                changeType: 'Added',
            })

            fieldState!.elements = [...newTextContentElement]
            setFieldState?.(fieldState)

            if (template?.contentItem && contentIndex! >= 0) {
                template.contentItem.elements?.splice(
                    contentIndex!,
                    1,
                    ...newTextContentElement
                )
                setProperty?.(template)

                insertPill?.({
                    fieldState,
                    template,
                    insertPillIndex:
                        contentIndex! +
                        (newTextContentElement?.length - 1 ?? 0),
                    pillType,
                    items: newTextContentElement,
                    textWithPill: 'textWithPill',
                })
            }
        }
        return
    }

    if (areEq(variant, TextVariants.newlyadded)) {
        //This block will execute when user  creates  a new Para
        //And then user creates a new pill without saving the actual para
        let index = target as HTMLInputElement
        let pillIndex = index.selectionStart ?? 0

        saveChanges?.([], (target as any)?.value, pillIndex, pillType, true)
        return
    }

    insertPill?.({
        fieldState,
        template,
        insertPillIndex,
        pillType,
    })
    return
}
